import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from './cn';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

const widths: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  size?: ModalSize;
  footer?: React.ReactNode;
  /** Oculta la X de cerrar (cuando la acción es obligatoria). */
  disableCloseButton?: boolean;
  /**
   * Abre el modal POR ENCIMA de los paneles laterales (z-[110] en vez de z-50). Hace falta cuando
   * se abre desde un panel que ya está sobre la página, como la ficha lateral del cuadro
   * (overlay z-[99], panel z-[100]): sin esto el modal queda detrás y sus botones no reciben clics.
   */
  elevated?: boolean;
  children: React.ReactNode;
}

/**
 * Modal base del design system. Reemplaza los 12+ modales duplicados
 * (Transaction, GastoEdit, Purchase, Sale, etc.) con una superficie consistente.
 * Cierra con Escape o clic en el overlay. Se renderiza vía portal (document.body).
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  size = 'md',
  footer,
  disableCloseButton,
  elevated,
  children,
}) => {
  // Cierre con Escape + bloqueo del scroll del body mientras está abierto.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className={cn('fixed inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4', elevated ? 'z-[110]' : 'z-50')}
      role="dialog"
      aria-modal="true"
    >
      <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm transition-opacity" onClick={onClose} aria-hidden="true" />
      <div
        className={cn(
          'relative z-10 w-full overflow-hidden rounded-t-lg sm:rounded-lg border border-hairline bg-paper shadow-overlay',
          'max-h-[92vh] flex flex-col',
          widths[size],
        )}
      >
        {(title || !disableCloseButton) && (
          <div className="flex items-start justify-between gap-4 border-b border-hairline px-5 py-4">
            <div className="min-w-0">
              {title && <h2 className="font-display text-lg font-semibold leading-tight text-ink">{title}</h2>}
              {description && <p className="mt-0.5 text-sm text-sage">{description}</p>}
            </div>
            {!disableCloseButton && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar"
                className="-mr-1 shrink-0 rounded-md p-1.5 text-sage transition-colors hover:bg-paper-2 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-mid/60"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-hairline bg-paper-2/50 px-5 py-3">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
